import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Clock, ArrowUpRight, Loader2 } from 'lucide-react';
import Seo from '../components/Seo';
import useReveal from '../hooks/useReveal';
import { useLang } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; } };

const PAGE_SIZE = 13;

const Blog = () => {
  const { lang } = useLang();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  useReveal(lang + posts.length);

  useEffect(() => {
    axios.get(`${API}/articles?limit=${PAGE_SIZE}`).then((r) => {
      setPosts(r.data);
      setHasMore(r.data.length === PAGE_SIZE);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const loadMore = () => {
    setLoadingMore(true);
    axios.get(`${API}/articles?limit=${PAGE_SIZE}&skip=${posts.length}`).then((r) => {
      setPosts((p) => [...p, ...r.data]);
      setHasMore(r.data.length === PAGE_SIZE);
    }).catch(() => {}).finally(() => setLoadingMore(false));
  };

  const [featured, ...rest] = posts;

  return (
    <div>
      <Seo title="The Journal — Ultra-Luxury Cosmetic Trends" path="/blog"
        description="Insights on ultra-luxury cosmetic trends, prestige formulation and private-label manufacturing from ARGE Cosmetics." />

      <section className="pt-40 pb-14 satin-cream text-center">
        <div className="max-w-3xl mx-auto px-6">
          <span className="inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase text-gold mb-4"><span className="w-8 h-px bg-gold" />The Journal</span>
          <h1 className="font-serif text-5xl md:text-6xl text-espresso leading-tight">Ultra-Luxury Cosmetic Trends</h1>
          <p className="mt-5 text-lg text-mocha">Editorial insight on prestige formulation, ingredient science and the business of beauty — fresh perspectives, published regularly.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="flex justify-center py-24 text-mocha"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : posts.length === 0 ? (
            <p className="text-center text-mocha py-24">Articles are being prepared — please check back shortly.</p>
          ) : (
            <>
              {featured && (
                <Link to={`/blog/${featured.slug}`} className="reveal group grid lg:grid-cols-2 gap-8 mb-16 items-center">
                  <div className="img-zoom aspect-[16/10] overflow-hidden shadow-[0_30px_70px_-30px_rgba(43,36,32,0.35)]">
                    <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <span className="text-[0.7rem] tracking-[0.22em] uppercase text-gold">Featured · {fmt(featured.published_at)}</span>
                    <h2 className="font-serif text-4xl text-espresso mt-3 leading-tight group-hover:text-gold transition-colors">{featured.title}</h2>
                    <p className="mt-4 text-mocha leading-relaxed">{featured.excerpt}</p>
                    <span className="inline-flex items-center gap-2 mt-5 text-xs tracking-[0.16em] uppercase text-espresso group-hover:text-gold">Read Article <ArrowUpRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {rest.map((p) => (
                  <Link to={`/blog/${p.slug}`} key={p.slug} className="reveal group flex flex-col hover-lift bg-ivory">
                    <div className="img-zoom aspect-[16/10] overflow-hidden"><img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" /></div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-3 text-[0.65rem] tracking-[0.15em] uppercase text-gold"><span>{fmt(p.published_at)}</span><span className="flex items-center gap-1 text-mocha"><Clock className="w-3 h-3" />{p.read_time} min</span></div>
                      <h3 className="font-serif text-2xl text-espresso mt-2 leading-snug group-hover:text-gold transition-colors">{p.title}</h3>
                      <p className="mt-3 text-sm text-mocha leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
              {hasMore && (
                <div className="text-center mt-14">
                  <button onClick={loadMore} disabled={loadingMore} data-testid="blog-load-more"
                    className="inline-flex items-center gap-3 px-12 py-4 border border-espresso/30 text-espresso text-xs tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors disabled:opacity-60">
                    {loadingMore ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</> : 'Load More Articles'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
